import React, { useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps, useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import classNames from 'classnames';

import { getToolbarLevel } from './mobileToolbarUtils';
import { preventDefaultOnly } from '../../utils/domUtil';
import editorStoreConnect from '../../components/editor/store/components/editorStoreConnect';
import {
    editedOrSelectedElementIdsSelector,
    editedOrSelectedElementsSelector,
    editorHasTextRangeSelectionSelector,
    getOnboardingDisableToolbar,
    selectedChildElementsSelector,
} from '../../workspace/toolbar/selector/toolbarSelector';
import { getPlatformDetailsSelector } from '../../platform/platformSelector';
import { isDesktopWebTesting } from '../../platform/utils/platformDetailsUtils';
import { getCurrentUser, getCurrentUserId } from '../../user/currentUserSelector';
import { getCurrentlyEditingEditorKey } from '../../element/selection/currentlyEditingSelector';
import { getAnyMatchingSheetOpen } from '../structural/sheet/sheetSelectors';
import { getParentTaskListThunk } from '../../element/task/taskActions';
import { isBoardLike } from '../../../common/elements/utils/elementTypeUtils';
import { getMobileBaseToolListItems } from './utils/mobileBaseToolbarUtils';
import { createToolbarList, getSelectedElementTypes } from '../../workspace/toolbar/utils/toolbarAvailableToolsUtils';
import { isNotElementEditorSheet } from '../structural/sheet/utils/sheetStateUtils';

import useDebouncedValue from '../../utils/react/useDebouncedValue';
import useToolbarState from '../../workspace/toolbar/hooks/useToolbarState';
import useToolbarTransitionManager from '../../workspace/toolbar/hooks/useToolbarTransitionManager';
import useDefaultToolbarType from './hooks/useRequestedToolbarType';
import useUpdateToolbarHeight from './hooks/useUpdateToolbarHeight';
import useMobileToolbarPositioner from './hooks/useMobileToolbarPositioner';
import useMobileToolbarColor from './hooks/useMobileToolbarColor';
import useToolbarReduxReporting from '../../workspace/toolbar/hooks/useToolbarReduxReporting';

import ToolbarErrorBoundary from '../../workspace/toolbar/components/ToolbarErrorBoundary';
import MobileToolbarToolList from './toolList/MobileToolbarToolList';
import ToastRenderer from '../../structural/toast/ToastRenderer';

import { MOBILE_TOOLBAR_HEIGHT } from './MobileToolbarConstants';
import { ImMNElement } from '../../../common/elements/elementModelTypes';
import { ToolbarListType } from '../../workspace/toolbar/toolbarTypes';
import { TOOLBAR_TOOLS } from '../../workspace/toolbar/config/toolbarToolConfig';

import './MobileToolbar.scss';

const TOOLBAR_PRIORITIES = [
    ToolbarListType.MOBILE_TABS,
    ToolbarListType.SELECTED,
    ToolbarListType.EDITING,
    ToolbarListType.EDITING_RANGE,
];

export enum RevealKeyboardState {
    NONE,
    PREPARING_REVEAL,
    REVEAL,
}

const mapStateToProps = createStructuredSelector({
    selectedElementIds: editedOrSelectedElementIdsSelector,
    selectedElements: editedOrSelectedElementsSelector,
    selectedChildElements: selectedChildElementsSelector,
    currentUserId: getCurrentUserId,
    currentUser: getCurrentUser,
    editorKey: getCurrentlyEditingEditorKey,
    onboardingDisableToolbar: getOnboardingDisableToolbar,
});

const mapDispatchToProps = (dispatch: Function) => ({
    dispatchGetSelectedParentTaskList: (elementId: string): ImMNElement | undefined =>
        dispatch(getParentTaskListThunk({ elementId })),
});

const mapEditorStateToProps = createStructuredSelector({
    hasEditorSelection: editorHasTextRangeSelectionSelector,
});

const connector = connect(mapStateToProps, mapDispatchToProps);
const editorConnector = editorStoreConnect(mapEditorStateToProps);

type ToolbarContainerProps = {
    currentBoardId: string;
    gridSize: number;
    gridSizeName: string;
    permissions: number;
    hasEditorSelection: boolean;
    toolbarRef: React.RefObject<HTMLDivElement>;
};

type ToolbarProps = ConnectedProps<typeof connector> & ToolbarContainerProps;

const getToolbarDebounceDuration = (
    selectedElements: ToolbarProps['selectedElements'],
    onboardingDisableToolbar: boolean,
): number => {
    // if we're in onboarding, disable toolbar updates
    if (onboardingDisableToolbar) return -1;

    // if the user has a single board selected, debounce for a longer time to prevent flickering when double clicking
    if (selectedElements.size === 1 && selectedElements.every(isBoardLike)) return 300;

    return 100;
};

/**
 * Root component for the toolbar.
 * Responsible for determining the toolbar size, rendering the toolbar items, and managing the toolbar state.
 * */
const MobileToolbar = (props: ToolbarProps) => {
    const { selectedElements, gridSizeName, gridSize, onboardingDisableToolbar, toolbarRef } = props;

    const platformDetails = useSelector(getPlatformDetailsSelector);

    // add a debounce to the props to prevent unnecessary rerenders & allow for any pending state changes to be applied
    // - -1ms is used for while toolbar is disabled in onboarding (will prevent all updates)
    // - 300ms is used for boards (in case the user is in the process of double clicking to navigate)
    // - 100ms for other elements (to prevent other toolbar flickers)
    const debouncedProps = useDebouncedValue(
        props,
        getToolbarDebounceDuration(selectedElements, onboardingDisableToolbar),
    );

    const [revealKeyboardState, setRevealKeyboardState] = useState<RevealKeyboardState>(RevealKeyboardState.NONE);

    const [defaultToolbarType, setDefaultToolbarType] = useDefaultToolbarType(ToolbarListType.MOBILE_TABS);

    const resetToolbar = () => setDefaultToolbarType(ToolbarListType.MOBILE_TABS);

    const selectedElementTypes = getSelectedElementTypes(debouncedProps.selectedElements);
    const baseToolLists = useMemo(
        () => getMobileBaseToolListItems(selectedElementTypes, debouncedProps.editorKey),
        [selectedElementTypes, debouncedProps.editorKey, debouncedProps.currentUser],
    );

    useEffect(() => {
        if (props.currentBoardId !== debouncedProps.currentBoardId) {
            resetToolbar();
        }
    }, [props.currentBoardId, debouncedProps.currentBoardId]);

    const toolbarState = useToolbarState({
        ...debouncedProps,
        defaultToolbarType: defaultToolbarType,
        toolbarTypePriorities: TOOLBAR_PRIORITIES,
        baseToolLists,
        initialToolbars: [
            createToolbarList(
                ToolbarListType.MOBILE_TABS,
                TOOLBAR_PRIORITIES,
                baseToolLists,
                debouncedProps.selectedElements,
                debouncedProps.selectedChildElements,
                debouncedProps.currentUser,
                debouncedProps.permissions,
                debouncedProps.editorKey,
                debouncedProps.hasEditorSelection,
            ),
        ],
    });

    useToolbarReduxReporting(toolbarState);

    const currentToolList = toolbarState.toolbars[toolbarState.focusIndex];
    const previousToolList = toolbarState.toolbars[toolbarState.focusIndex - 1];

    const currentToolListType = currentToolList?.type || ToolbarListType.MOBILE_TABS;
    const previousToolListType = previousToolList?.type || ToolbarListType.MOBILE_TABS;

    const currentToolbarLevel = getToolbarLevel(currentToolListType);
    const currentToolbarHeight = MOBILE_TOOLBAR_HEIGHT[currentToolbarLevel];

    const { renderedToolbars, progress, focusPosition } = useToolbarTransitionManager(
        toolbarState,
        currentToolbarHeight,
        {
            disableAnimation:
                revealKeyboardState === RevealKeyboardState.PREPARING_REVEAL ||
                revealKeyboardState === RevealKeyboardState.REVEAL,
        },
    );

    useUpdateToolbarHeight(currentToolbarHeight);
    useMobileToolbarPositioner(platformDetails, revealKeyboardState, setRevealKeyboardState);

    const toolbarColor = useMobileToolbarColor(currentToolListType, previousToolListType, progress);

    // Clicking on the toolbar while editing can trigger a blur which would finish editing.
    // This prevents it from happening, except when there are sheets open that don't contain editable content,
    // in which case we want to allow regular clicks and focus/blur events
    const nonEditorSheetsOpen = useSelector(getAnyMatchingSheetOpen(isNotElementEditorSheet));
    const clickHandler = nonEditorSheetsOpen || isDesktopWebTesting(platformDetails) ? undefined : preventDefaultOnly;

    const toolbarHeightStyle = useMemo(
        () => ({ '--toolbar-height': `${currentToolbarHeight}px` } as React.CSSProperties),
        [currentToolbarHeight],
    );

    // If the rendered tools list contains the hide toolbar tool, don't render the toolbar
    if (currentToolList.items.map((item) => item.id).includes(TOOLBAR_TOOLS.META_HIDE_TOOLBAR.id)) {
        return null;
    }

    return (
        <>
            <ToastRenderer style={toolbarHeightStyle} />
            <div
                ref={toolbarRef}
                id="toolbar"
                className={classNames('MobileToolbar', currentToolList?.type, {
                    'prepare-reveal-keyboard': revealKeyboardState === RevealKeyboardState.PREPARING_REVEAL,
                    'reveal-keyboard': revealKeyboardState === RevealKeyboardState.REVEAL,
                })}
                data-sheet-ignore-clicks
                style={
                    {
                        ...toolbarHeightStyle,
                        '--toolbar-color': toolbarColor,
                    } as React.CSSProperties
                }
                onPointerDown={clickHandler}
                onDoubleClick={clickHandler}
            >
                <div className="toolbar-window">
                    <div
                        className="tool-list-container"
                        style={{
                            height: renderedToolbars.length * currentToolbarHeight,
                            transform: `translate3d(0, ${-focusPosition}px, 0)`,
                        }}
                    >
                        {renderedToolbars.map((toolList, index) => {
                            return (
                                <div
                                    key={toolList.id}
                                    className={classNames('tool-list', toolList.type)}
                                    style={{
                                        left: 0,
                                        top: index * currentToolbarHeight,
                                    }}
                                >
                                    <MobileToolbarToolList
                                        toolList={toolList}
                                        index={index}
                                        gridSize={gridSize}
                                        gridSizeName={gridSizeName}
                                        debouncedProps={debouncedProps}
                                        setDefaultToolbarType={setDefaultToolbarType}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </>
    );
};

const ConnectedToolbar = connector(editorConnector(MobileToolbar));

const MobileToolbarContainer = (props: ToolbarContainerProps) => (
    <div className="MobileToolbarContainer">
        <ToolbarErrorBoundary>
            <ConnectedToolbar {...props} />
        </ToolbarErrorBoundary>
    </div>
);

export default MobileToolbarContainer;
